import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { KeyRound, FlaskConical, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, eur, dateFr } from "@/lib/api";
import { PageShell, StatusPill, Empty } from "@/components/common";
import { DeliveryQuoteForm } from "@/components/DeliveryQuoteForm";
import { useMe } from "@/context/MeContext";

export default function Livraison() {
  const api = useApi();
  const { isSignedIn } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (isSignedIn) {
      api.get("/deliveries").then((r) => setItems(r.data));
    }
  }, [api, isSignedIn]);
  return (
    <PageShell eyebrow="Coursier · sandbox" title="Livraison">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 bg-white rounded-2xl border border-stone-200 p-6 sm:p-8">
          <h2 className="font-serif text-2xl text-forest mb-1">Estimer une livraison</h2>
          <p className="text-sm text-stone-500 mb-6">La livraison se commande au moment du paiement d'une annonce réservée. L'estimation ci-dessous est indicative.</p>
          {isSignedIn ? <DeliveryQuoteForm /> : <Link to="/sign-in" className="underline" data-testid="delivery-signin-link">Connectez-vous pour estimer une livraison</Link>}
        </div>
        <div className="lg:col-span-5 space-y-3">
          <p className="eyebrow">Mes livraisons</p>
          {!items.length && <Empty testid="deliveries-empty">Aucune livraison en cours.</Empty>}
          {items.map((d) => (
            <Link key={d.id} to={`/livraison/${d.id}`} data-testid={`delivery-item-${d.id}`} className="block bg-white rounded-2xl border border-stone-200 p-4 hover:border-forest transition-colors">
              <div className="flex justify-between items-center"><p className="font-medium">{d.listing_title}</p><StatusPill status={d.status} /></div>
              <p className="text-xs text-stone-500 mt-1">{d.pickup_city} → {d.dropoff_city} · {d.provider_label}</p>
            </Link>
          ))}
        </div>
      </div>
    </PageShell>
  );
}

const SIM_NEXT = { requested: ["confirmed", "refused"], confirmed: ["courier_assigned"], courier_assigned: ["picked_up"], picked_up: ["in_transit"], in_transit: ["delivered", "recipient_absent"], recipient_absent: ["in_transit", "returned"] };
const SIM_LABEL = { confirmed: "Transporteur confirme", refused: "Transporteur refuse", courier_assigned: "Assigner un coursier", picked_up: "Collecte effectuée", in_transit: "En route", delivered: "Livrer (PIN)", recipient_absent: "Destinataire absent", returned: "Retour expéditeur" };

export function DeliveryDetail() {
  const { id } = useParams();
  const api = useApi();
  const { me } = useMe();
  const [d, setD] = useState(null);
  const [pin, setPin] = useState("");
  const [inc, setInc] = useState({ type: "damaged", description: "" });
  const load = useCallback(() => api.get(`/deliveries/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [api, id]);
  useEffect(() => { load(); }, [load]);

  const sim = async (status) => {
    try { const { data } = await api.post(`/deliveries/${id}/simulate`, { status, pin }); if (data.duplicate) toast.info("Événement déjà reçu"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!d) return <PageShell><p className="text-stone-500">Chargement…</p></PageShell>;
  const isBuyer = d.buyer_id === me?.user_id;
  return (
    <PageShell eyebrow={d.provider_label} title={d.listing_title}>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <div className="flex justify-between items-center mb-6"><p className="text-sm text-stone-600">{d.pickup_city} → {d.dropoff_city} · {d.slot}{d.relay_point && <><br /><span className="text-terra" data-testid="delivery-relay-point">Retrait : {d.relay_point}</span></>}</p><StatusPill status={d.status} testid="delivery-status" /></div>
            <ol className="relative border-l-2 border-stone-200 ml-2 space-y-5" data-testid="delivery-timeline">
              {d.events.map((e) => (
                <li key={e.id} className="ml-5">
                  <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${e.ignored ? "bg-stone-300" : "bg-forest"}`} />
                  <p className="text-sm font-medium">{e.label}{e.ignored && " (ignoré : transition invalide)"}</p>
                  <p className="text-xs text-stone-500">{dateFr(e.at)}</p>
                </li>
              ))}
            </ol>
            {d.proof && <p className="mt-6 text-sm text-emerald-700" data-testid="delivery-proof">Preuve de livraison : code PIN validé le {dateFr(d.proof.validated_at)}</p>}
          </div>
          {isBuyer && d.pin && d.status !== "delivered" && (
            <div className="bg-forest text-sand rounded-2xl p-6 flex items-center gap-4" data-testid="delivery-pin-box">
              <KeyRound className="w-6 h-6" />
              <div><p className="text-sm opacity-80">Code de remise à donner au coursier</p><p className="font-serif text-4xl tracking-[0.3em]" data-testid="delivery-pin">{d.pin}</p></div>
            </div>
          )}
        </div>
        <div className="lg:col-span-5 space-y-6">
          {SIM_NEXT[d.status] && (
            <div className="bg-muted rounded-2xl p-6 space-y-3" data-testid="delivery-simulator">
              <p className="font-semibold flex items-center gap-2 text-forest"><FlaskConical className="w-4 h-4" />Simulateur transporteur (webhook signé)</p>
              {SIM_NEXT[d.status].includes("delivered") && <Input data-testid="simulate-pin-input" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN du destinataire" className="bg-white" />}
              <div className="flex flex-wrap gap-2">
                {SIM_NEXT[d.status].map((s) => <Button key={s} data-testid={`simulate-${s}-btn`} variant="outline" className="rounded-full bg-white" onClick={() => sim(s)}>{SIM_LABEL[s]}</Button>)}
              </div>
            </div>
          )}
          {["requested", "confirmed", "courier_assigned"].includes(d.status) && (
            <Button data-testid="delivery-cancel-btn" variant="outline" className="rounded-full w-full" onClick={() => act(() => api.post(`/deliveries/${id}/cancel`), "Livraison annulée — remboursement à traiter")}>Annuler la livraison</Button>
          )}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-3">
            <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-terra" />Déclarer un incident</p>
            <Select value={inc.type} onValueChange={(v) => setInc({ ...inc, type: v })}>
              <SelectTrigger data-testid="incident-type-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[["damaged", "Objet endommagé"], ["lost", "Objet perdu"], ["late", "Retard"], ["recipient_absent", "Absence du destinataire"], ["other", "Autre"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="incident-description-input" value={inc.description} onChange={(e) => setInc({ ...inc, description: e.target.value })} placeholder="Décrivez le problème" />
            <Button data-testid="incident-submit-btn" className="rounded-full bg-terra hover:bg-terra/90" onClick={() => act(() => api.post(`/deliveries/${id}/incident`, inc), "Incident transmis au support")}>Envoyer</Button>
            <p className="text-xs text-stone-500">Prix payé : {eur(d.price_ttc)}. Toute indemnisation dépend des conditions contractuelles du transporteur.</p>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
